import { BOOLEAN_SELECT_LIST_ITEMS, Box, CancelIconButton, Grid, Stack, Tooltip, Typography } from "@compass/core";
import InvalidIcon from "@mui/icons-material/Block";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { DatePicker, Input, ReportPropertyIcon, Select, TimePicker } from "components";
import { CustomFilter as CustomFilterModel, ReportPropertyBase, ReportPropertyType } from "generated/openApi";
import produce from "immer";
import { useEffect, useState } from "react";
import { customFilterOperatorOptions } from "./operators";
import { getOperatorOptions, validateCustomFilter } from "./services";

export const CustomFilter = (props: {
  filter: CustomFilterModel;
  setFilter: (filter: CustomFilterModel) => void;
  properties: ReportPropertyBase[] | undefined;
  removeFilter: () => void;
}) => {
  const selectedProperty = props.filter.property;
  const selectedOperatorOption = customFilterOperatorOptions?.find((x) => x.operator === props.filter.operator);
  const selectableOperatorOptions = getOperatorOptions(selectedProperty);

  const [inputValue, setInputValue] = useState<string>("");

  useEffect(() => {
    props.setFilter(
      produce(props.filter, (draft) => {
        const errorMessage = validateCustomFilter(props.filter, customFilterOperatorOptions);
        draft.isValid = !errorMessage;
      })
    );
  }, [props.filter.property, props.filter.operator, props.filter.values]);

  const renderValueBox = () => {
    const selectedPropertyType = selectedProperty?.propertyType;
    const hasSelectList = selectedProperty?.selectList && selectedProperty?.selectList?.length > 0;

    if (hasSelectList) {
      return (
        <Select
          multiple
          label={"Multiple Values"}
          value={selectedProperty?.selectList?.filter((x) => props.filter?.values?.includes(x.id.toString()))}
          getOptionLabel={(option) => option.name}
          onChange={(e, values) => {
            props.setFilter(
              produce(props.filter, (draft) => {
                draft.values = values.map((x) => x.id.toString());
              })
            );
          }}
          options={selectedProperty?.selectList}
          placeholder="Select values"
          //size="small"
        />
      );
    }

    if (
      selectedPropertyType === ReportPropertyType.String ||
      selectedPropertyType === ReportPropertyType.Int ||
      selectedPropertyType === ReportPropertyType.Decimal
    ) {
      if (selectedOperatorOption?.filterValueType === "Multiple")
        return (
          <Select
            multiple
            freeSolo
            label={"Multiple Values"}
            inputValue={inputValue}
            value={props.filter?.values}
            getOptionLabel={(option) => option || ""}
            onChange={(e, values) => {
              props.setFilter(
                produce(props.filter, (draft) => {
                  draft.values = values;
                })
              );
              setInputValue("");
            }}
            onInputChange={(e) => setInputValue(e.target.value)}
            options={
              selectedOperatorOption.canUseSelectList ? selectedProperty?.selectList?.map((x) => x.name) : undefined
            }
            onBlur={() => {
              props.setFilter(
                produce(props.filter, (draft) => {
                  if (inputValue) {
                    if (!draft.values) draft.values = [];
                    draft.values.push(inputValue);
                  }
                })
              );
              setInputValue("");
            }}
            placeholder="Type each value and press enter"
            //size="small"
          />
        );

      if (selectedOperatorOption?.filterValueType === "Single")
        return (
          <Input
            label={"Value"}
            value={props.filter?.values?.[0]}
            onChange={(e) => {
              props.setFilter(
                produce(props.filter, (draft) => {
                  draft.values = e.target.value ? [e.target.value] : [];
                })
              );
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                // prevent enter from submitting the form
                event.preventDefault();
              }
            }}
            //size="small"
          />
        );
    }

    if (selectedPropertyType === ReportPropertyType.Date || selectedPropertyType === ReportPropertyType.DateTimeOffset)
      return (
        <DatePicker
          label={"Value"}
          value={props.filter?.values?.[0]}
          onChange={(value) =>
            props.setFilter(
              produce(props.filter, (draft) => {
                draft.values = value ? [value] : [];
              })
            )
          }
          //size="small"
        />
      );

    if (selectedPropertyType === ReportPropertyType.Bool || selectedPropertyType === ReportPropertyType.NullableBool)
      return (
        <Select
          label={"Value"}
          value={BOOLEAN_SELECT_LIST_ITEMS.find((x) => x.id.toString() === props.filter?.values?.[0])}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) => {
            props.setFilter(
              produce(props.filter, (draft) => {
                draft.values = [value.id.toString()];
              })
            );
          }}
          options={BOOLEAN_SELECT_LIST_ITEMS}
          //size="small"
        />
      );

    if (selectedPropertyType === ReportPropertyType.TimeSpan)
      return (
        <TimePicker
          label="Value"
          value={props.filter?.values?.[0]}
          onChange={(value) =>
            props.setFilter(
              produce(props.filter, (draft) => {
                draft.values = [value];
              })
            )
          }
          //size="small"
        />
      );

    // otherwise return a placeholder
    return (
      <Input
        label={"Value"}
        disabled
        //size="small"
      />
    );
  };

  return (
    <Box width="100%">
      <Grid container spacing="sm" width="100%">
        {/* Filter Status */}
        <Grid item display="inline-flex" alignItems={"center"}>
          {props.filter.isValid ? (
            <CheckBoxIcon color="success" />
          ) : (
            <Tooltip
              title={
                <Stack>
                  <Typography>This filter is not valid</Typography>
                  <Typography variant="caption">
                    {validateCustomFilter(props.filter, customFilterOperatorOptions)}
                  </Typography>
                </Stack>
              }
            >
              <InvalidIcon color="error" />
            </Tooltip>
          )}
        </Grid>

        {/* Filter Field */}
        <Grid item flexGrow={1} flexBasis={0}>
          <Select
            //autoFocus={selectedField === undefined}
            //openOnFocus={selectedField === undefined}
            label="Field"
            value={selectedProperty}
            options={props.properties}
            getOptionLabel={(option) => option.displayName!}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                <Stack width="100%" direction="row" spacing="sm" alignItems={"center"}>
                  <ReportPropertyIcon property={option} />
                  <Typography variant="inherit">{option.displayName}</Typography>
                </Stack>
              </Box>
            )}
            onChange={(e, value) =>
              props.setFilter(
                produce(props.filter, (draft) => {
                  draft.property = value;
                  draft.propertyType = value.propertyType ?? ReportPropertyType.String;
                  draft.values = [];
                })
              )
            }
            autoComplete="off"
            //size="small"
          />
        </Grid>

        {/* Filter Operator */}
        <Grid item flexGrow={1} flexBasis={0}>
          <Select
            label="Operator"
            value={selectableOperatorOptions?.find((x) => x.operator === props.filter.operator)}
            options={selectableOperatorOptions}
            getOptionLabel={(option) => option.description!}
            onChange={(e, value) =>
              props.setFilter(
                produce(props.filter, (draft) => {
                  draft.operator = value.operator;
                })
              )
            }
            noOptionsText={!selectedProperty && "No selected field"}
            autoComplete="off"
            //size="small"
          />
        </Grid>

        {/* Filter Value Box: has many variants */}
        <Grid item flexGrow={2} flexBasis={0}>
          {selectedOperatorOption?.filterValueType !== "None" && renderValueBox()}
          {/* {selectedOperatorOption?.filterValueType === "None" &&
            selectedField?.dataType === "String" &&
            renderNullStringBox()} */}
        </Grid>

        {/* Filter Actions */}
        <Grid item display="inline-flex" alignItems={"center"}>
          <Stack direction="row" spacing="sm" alignItems={"center"}>
            <CancelIconButton onClick={() => props.removeFilter()} />
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};
